import { createContext, useContext, useEffect, useRef, type ReactNode } from 'react';
import type { SyncEngine } from '../sync/SyncEngine';
import { initSyncTriggers } from '../sync/triggers';

const SyncContext = createContext<SyncEngine | null>(null);

export interface SyncProviderProps {
  readonly engine: SyncEngine;
  readonly syncIntervalMs?: number;
  readonly children: ReactNode;
}

/**
 * Provides a SyncEngine instance to the React tree.
 * Initializes sync triggers on mount and cleans up on unmount.
 */
export function SyncProvider({ engine, syncIntervalMs, children }: SyncProviderProps) {
  const cleanupRef = useRef<(() => void) | null>(null);

  useEffect(() => {
    cleanupRef.current = initSyncTriggers(() => { engine.sync(); }, syncIntervalMs);
    return () => { cleanupRef.current?.(); };
  }, [engine, syncIntervalMs]);

  return <SyncContext.Provider value={engine}>{children}</SyncContext.Provider>;
}

/** Access the SyncEngine from any component inside <SyncProvider>. */
export function useSyncEngine(): SyncEngine | null {
  return useContext(SyncContext);
}
